<template>
  <div class="max-w-6xl h-full m-auto py-4 px-4 flex flex-col space-y-4">
    <div class="flex flex-row justify-between space-x-4 py-2">
      <h2 class="text-2xl">On Hold Summary</h2>

      <div class="flex space-x-4 items-center">
        <Button
          v-on:click-handler="exportCSV()"
          class="btn btn-primary btn-small flex space-x-2 items-center"
          :disabled="isLoading || !gridHasData"
        >
          <span>Download CSV</span>
          <icon set="outline" name="document-download" class="h-5 w-5" />
        </Button>
        <Button
          v-on:click-handler="loadReport()"
          class="btn btn-primary btn-small flex space-x-2 items-center"
          :disabled="isLoading"
        >
          <span>Refresh Report</span>
          <LoadingSpinner v-if="isLoading" class="h-5 w-5" />
        </Button>
      </div>
    </div>

    <ag-grid-vue
      style="height: 100%; width: 100%;"
      class="ag-theme-balham"
      :columnDefs="columnDefs"
      :rowData="rowData"
      :paginationAutoPageSize="false"
      :paginationPageSize="paginationPageSize"
      :pagination="true"
      id="tasks-report"
      :gridOptions="gridOptions"
      @grid-ready="onGridReady"
      @first-data-rendered="onFirstDataRendered"
      :context="context"
      :frameworkComponents="frameworkComponents"
      :defaultColDef="defaultColDef"
      :enableCellTextSelection="true"
      :ensureDomOrder="true"
    >
    </ag-grid-vue>
  </div>
</template>
<script>
import { AgGridVue } from "ag-grid-vue";
import OrderLink from "./components/OrderLink";
import { ApiFactory } from "@/api/ApiFactory.js";
import Button from "@/components/elements/Button";
import dateFormatter from "@/mixins/dateFormatter";
import moneyFormatter from "@/mixins/moneyFormatter";

const Reports = ApiFactory.get("reports");

export default {
  name: "OnHoldSummary",
  components: {
    Button,
    AgGridVue,
    OrderLink, // eslint-disable-line
  },
  mixins: [dateFormatter, moneyFormatter],
  props: {},
  data() {
    return {
      columnDefs: null,
      rowData: null,
      paginationPageSize: 100,
      frameworkComponents: null,
      gridApi: null,
      columnApi: null,
      gridOptions: null,
      context: null,
      defaultColDef: null,
      overlayLoadingTemplate: true,
      isLoading: false,
    };
  },
  computed: {
    gridHasData() {
      return this.rowData && this.rowData.length;
    }
  },
  beforeMount() {
    this.gridOptions = {};

    this.context = { componentParent: this };

    this.defaultColDef = {
      sortable: true,
      filter: true,
      suppressMovable: true,
      resizable: true,
    };

    this.columnDefs = [
      {
        headerName: "Order Number",
        field: "SourceOrderNumber",
        cellRendererFramework: "OrderLink",
        cellRendererParams: {
          target: "_blank"
        }
      },
      {
        headerName: "Fulfillment Status",
        field: "Status"
      },
      {
        headerName: "Name",
        field: "ShipName"
      },
      {
        headerName: "Order Date",
        field: "OrderDate",
        valueFormatter(params) {
          return dateFormatter.format(params.value);
        }
      },
      {
        headerName: "Items Not Shipped",
        field: "ItemsNotShipped",
        filter: "agNumberColumnFilter"
      },
      {
        headerName: "Total",
        field: "FinalGrandTotal",
        valueFormatter(params) {
          return moneyFormatter.format(params.value);
        },
        filter: "agNumberColumnFilter"
      },
      {
        headerName: "Note Date",
        field: "EntryDate",
        valueFormatter(params) {
          return dateFormatter.format(params.value);
        },
        filter: "agDateColumnFilter"
      },
      {
        headerName: "User",
        field: "EnteredBy"
      },
      {
        headerName: "Note",
        field: "Notes"
      }
    ];

    this.loadReport();
  },
  mounted() {
    this.gridApi = this.gridOptions.api;
    this.gridColumnApi = this.gridOptions.columnApi;
  },
  methods: {
    loadReport() {
      this.isLoading = true;
      this.gridOptions?.api?.showLoadingOverlay();

      Reports.getOnHoldSummary()
        .then((response) => {
          this.rowData = response.data.body.map((data) => {
            let {
              OrderDate,
              EntryDate,
              ...properties
            } = data;

            return {
              ...properties,
              OrderDate: this.setDate(OrderDate),
              EntryDate: this.setDate(EntryDate)
            };
          })
        })
        .finally(() => (this.isLoading = false));
    },
    setDate(value) {
      if (!value) {
        return null;
      }

      return new Date(value);
    },
    exportCSV() {
      if (! this.gridHasData) return;
      this.gridApi.exportDataAsCsv({
        fileName: this.$options.name + "-" + dateFormatter.format(new Date())
      });
    },
    onFirstDataRendered(params) {
      params.api.sizeColumnsToFit();
    },
    onGridReady() {}
  },
};
</script>
<style lang="scss">
@import "../../../node_modules/ag-grid-community/dist/styles/ag-grid.css";
@import "../../../node_modules/ag-grid-community/dist/styles/ag-theme-balham.css";
</style>
